import { Dropdown, Menu, Skeleton, Tooltip } from 'antd';
import { Tabs } from 'antd';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, CrossSvg, FilterSvg, SearchSvg } from '../../../../assets/icons';
import ArrowRightSvg from '../../../../assets/icons/ArrowRightSvg';
import ShareSvg from '../../../../assets/icons/ShareSvg';
import theme from '../../../../assets/theme';
import { UserAccessBadge } from '../../../../hooks/useAccessBadge';
import useConsumeGenerator from '../../../../hooks/useConsumeGenerator';
import { useDesignerDataContext } from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  GeneratorTabState,
  TabState,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import useGeneratorsDesignerSearch from '../../../../hooks/useGeneratorsDesignerSearch';
import useGeneratorsDesignerSearchFilter from '../../../../hooks/useGeneratorsDesignerSearchFilter';
import useMixpanelFlag from '../../../../hooks/useMixpanelFlag';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import useRecentGenerators from '../../../../hooks/useRecentGenerators';
import { useRecommendedGenerators } from '../../../../hooks/useRecommendedGenerators';
import { useSingleGenerator } from '../../../../hooks/useSSRGeneratorNew';
import { LessonAttachment } from '../../../../services/backendService/backendService';
import { PromptInputData } from '../../../../services/backendService/types';
import { GeneratorCoarseLicenseLevel } from '../../../../services/TemplateService/Template.type';
import { generatorFormStateToUserInputFormData } from '../../../../utils/aiGeneratorUtils';
import {
  ContentCategory,
  subjectOptions,
} from '../../../../utils/authenticatedHomeUtils';
import { CURIPOD_FILTER_GRADE_OPTIONS } from '../../../../utils/general';
import { toIcon } from '../../../../utils/roundUtils';
import { zIndexes } from '../../../../utils/zIndexes';
import MultiSelect from '../../../_atoms/_MultiSelect/MultiSelect';
import Select from '../../../_atoms/_Select/Select';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import PlanTypeLabel from '../../../_atoms/PlanTypeLabel';
import ShowMoreButton from '../../../_atoms/ShowMoreButton/ShowMoreButton';
import BaseModal from '../../../_molecules/BaseModal';
import { GeneratorCard } from '../../../_molecules/GeneratorCard';
import { HorisontalGeneratorCard } from '../../../_molecules/HorisontalGeneratorCard';
import ProgressBar from '../../../_molecules/ProgressBar';
import { BackTitleGrid } from '../../../_molecules/RoundTypeSelector';
import ShareGlobalLesson from '../../../_molecules/ShareGlobalLesson';
import { Slide, SlideContentType, SlideType } from '../../../SlideComponents/slide.types';
import { createNewEmptySlide, slideTypeToIcon } from '../../../SlideComponents/slideUtil';
import SlideWrapperViwer from '../../../SlideComponents/SlideWrapperViewer';
import { TabContainer } from '../../DesignerTabs/DesignerTabUtils';
import {
  GeneratorForm,
  GeneratorFormState,
  useGeneratorFormState,
} from '../PromptsTab/GeneratorForm';
import GeneratorSelectSlides from './GeneratorSelectSlides';
import { ConsumerGenerator } from './utils';

const SearchInput = ({
  value,
  onChange,
  gradeOptions,
  subjectOptions,
  grades,
  subjectId,
  handleFilterChanged,
  handleClearFilters,
}: {
  value?: string;
  onChange: (val?: string) => void;
  subjectId?: string;
  subjectOptions: { value: string; displayName: string }[];
  grades?: string[];
  gradeOptions: { value: string; displayName: string }[];
  handleFilterChanged: (data: { subjectId?: string; grades?: string[] }) => void;
  handleClearFilters: () => void;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const handleSearchValueChanged = (value?: string) => {
    onChange(value);
    if (showDropdown) {
      setShowDropdown(false);
    }
  };

  const hasActiveFilters = subjectId || (grades && grades.length > 0);

  const FilterMenu = () => {
    return (
      <Menu
        style={{ padding: '20px 10px 10px 10px' }}
        className="generatorsFilterSelector"
      >
        <div style={{ marginBottom: 20 }}>
          <p style={{ marginBottom: 2 }}>Subject</p>
          <Select
            value={subjectId}
            options={subjectOptions}
            className="generatorsFilterSelector"
            onSelect={selectedSubjectId =>
              handleFilterChanged({
                subjectId: selectedSubjectId as string,
                grades: grades,
              })
            }
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <p style={{ marginBottom: 2 }}>Grades</p>
          <MultiSelect
            value={grades}
            options={gradeOptions}
            onChange={selectedGrades =>
              handleFilterChanged({
                subjectId: subjectId,
                grades: selectedGrades as string[],
              })
            }
          />
        </div>
        <div>
          <Button
            width="100%"
            height="42px"
            type="tertiary"
            styles={{ color: theme.colors.black, borderColor: theme.colors.black }}
            onClick={() => {
              handleClearFilters();
              setShowDropdown(false);
            }}
          >
            Clear filters
          </Button>
        </div>
      </Menu>
    );
  };

  return (
    <Dropdown
      placement="bottomRight"
      visible={showDropdown}
      onVisibleChange={visible => {
        // only hide it. The only place where it becomes visible is when you
        // click the filter button
        if (!visible) setShowDropdown(false);
      }}
      overlay={FilterMenu}
    >
      <SearchInputContainer>
        <IconWrapper
          style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
          height="20px"
          width="20px"
          color="#5B5B5A"
        >
          <SearchSvg />
        </IconWrapper>
        <SearchInputComp
          placeholder={'Search all customizable lessons'}
          value={value || ''}
          onChange={e => handleSearchValueChanged(e.target.value)}
        />
        {value && value.length > 0 && (
          <IconWrapper
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            onClick={() => onChange(undefined)}
            height="15px"
            width="15px"
            color="#5B5B5A"
          >
            <CrossSvg />
          </IconWrapper>
        )}
        {(!value || value.length === 0) && (
          <div
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <IconWrapper
              style={{
                position: 'relative',
              }}
              onClick={() => setShowDropdown(!showDropdown)}
              height="18px"
              width="18px"
              color="#5B5B5A"
            >
              {hasActiveFilters && (
                <ActiveFilterIndicationContainer> </ActiveFilterIndicationContainer>
              )}
              <FilterSvg />
            </IconWrapper>
          </div>
        )}
      </SearchInputContainer>
    </Dropdown>
  );
};

const ActiveFilterIndicationContainer = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  display: flex;
  height: 8px;
  width: 8px;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.orange};
  color: white;
  border-radius: 50px;
`;

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  height: 42px;
`;

const SearchInputComp = styled.input`
  border-radius: 5px;
  background: #f3f3f1;
  outline: none;
  border: none;
  height: 100%;
  width: 100%;
  padding-left: 40px;
  font-weight: bold;
  color: #5b5b5a;
`;

const GeneratorFilters = ({
  activeCategoryId,
  visibleCategories,
  onSelect,
}: {
  activeCategoryId: string;
  onSelect: (activeCategoryId: string) => void;
  visibleCategories: ContentCategory[];
}) => {
  const currentIndex = useRef(0); // used to know which index we're at to find the next or previous item for scrolling
  const currentScroll = useRef(0); // always know how much scroll we've done

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useEffect(() => {
    if (visibleCategories) {
      // reset currentIndex every time visibleCategories changes
      currentIndex.current = 0;
    }
  }, [visibleCategories]);
  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      const nodeWidths = allNodes.reduce(
        (acc, cur) => {
          //@ts-ignore
          const nodeWidth = cur.clientWidth;
          const gap = 10;
          const currentWidth = acc.currentWidth + nodeWidth + gap;
          return {
            currentWidth,
            items: [...acc.items, { start: acc.currentWidth, end: currentWidth }],
          };
        },
        {
          currentWidth: 0,
          items: [] as { start: number; end: number }[],
        },
      );
      const currentNodeIndex = nodeWidths.items.findIndex(({ start, end }) => {
        if (currentScroll.current >= start && currentScroll.current <= end) {
          return true;
        }
      });

      const index = currentNodeIndex + 3;

      const el = index > nodes.length ? nodes[nodes.length - 1] : nodes[index];
      if (el) {
        //@ts-ignore
        el.scrollIntoView({ behavior: 'smooth', inline: 'end' });
        currentIndex.current = index;
      }
    }
  };
  const handleLeftArrowClicked = () => {
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      const nodeWidths = allNodes.reduce(
        (acc, cur) => {
          //@ts-ignore
          const nodeWidth = cur.clientWidth;
          const gap = 10;
          const currentWidth = acc.currentWidth + nodeWidth + gap;
          return {
            currentWidth,
            items: [...acc.items, { start: acc.currentWidth, end: currentWidth }],
          };
        },
        {
          currentWidth: 0,
          items: [] as { start: number; end: number }[],
        },
      );
      const currentNodeIndex = nodeWidths.items.findIndex(({ start, end }) => {
        if (currentScroll.current >= start && currentScroll.current <= end) {
          return true;
        }
      });

      const index = currentNodeIndex - 3;

      const el = index < 0 ? nodes[0] : nodes[index];
      if (el) {
        //@ts-ignore
        el.scrollIntoView({ behavior: 'smooth', inline: 'start' });
        currentIndex.current = index;
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  const handleSelect = (activeCategoryId: string, e: any) => {
    const index = visibleCategories.findIndex(r => r.id === activeCategoryId);
    if (index !== -1) {
      currentIndex.current = index;
    }

    onSelect(activeCategoryId);
    e.target.scrollIntoView({ behavior: 'smooth', inline: 'center' });
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      currentScroll.current = ev.target.scrollLeft;
      if (currentScroll.current === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (currentScroll.current + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, []);

  return (
    <GeneratorsFilterMainContainer>
      {showLeftArrow && (
        <LeftArrowContainer onClick={handleLeftArrowClicked}>
          <div
            style={{
              height: '100%',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <IconWrapper
              style={{ marginLeft: -5 }}
              color={theme.colors.black}
              height="32px"
              width="32px"
            >
              <ArrowLeftSvg />
            </IconWrapper>
          </div>
        </LeftArrowContainer>
      )}
      <GeneratorFiltersContainer ref={listRef}>
        {visibleCategories.map(category => (
          <FilterButton
            $isActive={activeCategoryId === category.id}
            key={category.id}
            onClick={e => handleSelect(category.id, e)}
          >
            <span>{category.title}</span>
          </FilterButton>
        ))}
      </GeneratorFiltersContainer>
      {showRightArrow && (
        <RightArrowContainer onClick={handleRightArrowClicked}>
          <div
            style={{
              height: '100%',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <IconWrapper
              style={{ marginRight: -5 }}
              color={theme.colors.black}
              height="32px"
              width="32px"
            >
              <ArrowRightSvg />
            </IconWrapper>
          </div>
        </RightArrowContainer>
      )}
    </GeneratorsFilterMainContainer>
  );
};

const LeftArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 21px);
  width: 16px;
  left: 0;
  top: 10px;
  background: linear-gradient(to left, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  z-index: 1;
`;
const GeneratorsLeftArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 10px);
  width: 16px;
  left: 0;
  top: 0;
  background: linear-gradient(to left, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  z-index: 1;
`;

const RightArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 21px);
  width: 16px;
  right: 0;
  top: 10px;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  z-index: 1;
`;

const GeneratorsRightArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 10px);
  width: 16px;
  right: 0;
  top: 0;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  z-index: 1;
`;

const FilterButton = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 10px 20px;
  border: 1px solid ${theme.colors.black};
  border-radius: 5px;
  color: ${theme.colors.black};
  background: transparent;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:focus {
    outline: none;
  }

  span {
    width: 100%;
    white-space: nowrap;
  }

  color: ${theme.colors.black};
  background: transparent;

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: ${theme.colors.orange};
      color: white;
      border: 1px solid transparent;
    `}
`;

const GeneratorsFilterMainContainer = styled.div`
  position: relative;
  width: 100%;
  height: 65px;
`;

const GeneratorFiltersContainer = styled.ul`
  position: relative;
  width: 100%;
  overflow-x: auto;
  display: flex;
  gap: 10px;
  padding-top: 10px;
  padding-bottom: 15px;
  padding-left: 0px;
  padding-right: 0px;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }
`;

const GeneratorsForm = ({
  selectedGenerator,
  isLoading,
  isError,
  error,
  handleConsumeGenerator,
  formState: existingFormState,
}: {
  selectedGenerator: ConsumerGenerator;
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  handleConsumeGenerator: (
    formState: GeneratorFormState,
    generator?: ConsumerGenerator,
  ) => void;
  formState?: GeneratorFormState;
}) => {
  const { handleInputChange, formState } = useGeneratorFormState(
    selectedGenerator.aiContent?.promptInputs,
    existingFormState,
  );

  const handleSubmit = useCallback(() => {
    handleConsumeGenerator(formState, selectedGenerator);
  }, [formState, handleConsumeGenerator, selectedGenerator]);

  return (
    <GeneratorForm
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmit}
      promptInputs={selectedGenerator.aiContent?.promptInputs || []}
      formState={formState}
      promptInputSuggestions={selectedGenerator.aiContent?.promptInputSuggestions || []}
      isLoading={isLoading}
      isError={isError}
      error={error}
      isPremiumGenerator={selectedGenerator.aiContent?.isPremiumGenerator}
      generatorPlan={selectedGenerator.aiContent?.generatorPlan}
      generatorId={selectedGenerator.id}
      upgradeLocation="GeneratorsTab"
    />
  );
};

const DesignerGeneratorSelector = ({
  generatorTabState,
  setGeneratorTabState,
  onAddActivity,
  consumeGenerator,
  isLoading,
  isError,
}: {
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  generatorTabState: GeneratorTabState;
  onAddActivity: (slides: Slide[]) => void;
  consumeGenerator: (data: {
    generatorId: string;
    generatorName?: string;
    generatorPlan?: GeneratorCoarseLicenseLevel | null | undefined;
    userInputFormData: PromptInputData[];
    onSuccessFunction?: (data: {
      slides: Slide[];
      attachments?: LessonAttachment[];
    }) => void;
  }) => void;
  isLoading: boolean;
  isError: boolean;
}) => {
  const { t } = useTranslation('Host');
  const {
    handleGeneratorSearchChange,
    activeCategory,
    handleCategoryChanged,
    searchPhrase,
    grades,
    subjectId,
    handleFiltersChanged,
    visibleCategories,
    handleClearFilters,
  } = useGeneratorsDesignerSearchFilter();

  const [searchValue, setSearchValue] = useState<string>();

  const onSearchInputChange = (value?: string) => {
    setSearchValue(value);
    handleGeneratorSearchChange(value);
  };

  const onCategoryChange = (categoryId: string) => {
    handleCategoryChanged({ categoryId });
  };

  const [showShareModal, setShowShareModal] = useState(false);
  const [showMoreDescription, setShowMoreDescription] = useReducer(
    exists => !exists,
    false,
  );
  const { userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const handleConsumeGenerator = (
    formState: GeneratorFormState,
    generator?: ConsumerGenerator,
  ) => {
    if (generator?.id && generator?.aiContent?.promptInputs) {
      const userInputFormData = generatorFormStateToUserInputFormData(
        formState,
        generator.aiContent.promptInputs,
      );

      consumeGenerator({
        generatorId: generator.id,
        userInputFormData: userInputFormData || [],
        generatorName: generator.title || '',
        generatorPlan: generator?.aiContent?.generatorPlan,
        onSuccessFunction: ({
          slides,
          attachments,
        }: {
          slides: Slide[];
          attachments?: LessonAttachment[];
        }) => {
          setGeneratorTabState({
            ...generatorTabState,
            generatedSlides: slides,
            generatedAttachments: attachments,
            generatorFormState: formState,
          });
        },
      });
    }
  };

  const router = useRouter();

  const localTemplate = useDesignerDataContext(c => c.state.localTemplate);

  // This will fetch only when the generatorId is in the url, and that will
  // most likely be instant because of react query cache since you come from the
  // generator page
  const { generator: justConsumedGenerator } = useSingleGenerator(
    router.query.generatorId as string | undefined,
  );

  useEffect(() => {
    // make it easy to retry the generator by showing the form
    const fn = async () => {
      if (
        generatorTabState.generatedSlides.length > 0 ||
        generatorTabState.generatorFormState ||
        generatorTabState.selectedGeneratorNew
      ) {
        // If you have super slow internet and have already started doing things, we just fail silently
        return;
      }
      if (router.query.generatorId && localTemplate && justConsumedGenerator) {
        // We retrieve the stored generator and formstate from localstorage
        let cacheData = undefined;
        try {
          const cache = localStorage.getItem('@curipod_v1_recentGeneratorConsume');
          cacheData = cache
            ? (JSON.parse(cache) as {
                generatorId: string;
                formState: GeneratorFormState;
              })
            : undefined;
        } catch (e) {
          // We fail silently
          return;
        } finally {
          localStorage.removeItem('@curipod_v1_recentGeneratorConsume');
        }

        if (router.query.generatorId !== cacheData?.generatorId) {
          return; // if the generatorId is not the same as the one in the cache, we fail silently
        }

        setGeneratorTabState({
          generatedSlides: localTemplate?.content.slides || [],
          generatorFormState: cacheData?.formState,
          selectedGeneratorNew: justConsumedGenerator,
          isLoadingGeneratedRounds: false,
          isErrorGeneratedRounds: undefined,
        });

        // Regardless of how it goes we clean the url state
        router.replace(router.asPath.split('?')[0]); // clean the url state
      }
    };
    fn();
  }, [
    setGeneratorTabState,
    router,
    localTemplate,
    justConsumedGenerator,
    generatorTabState,
  ]);

  if (generatorTabState.generatedSlides && generatorTabState.generatedSlides.length !== 0)
    return (
      <GeneratorSelectSlides
        onAddActivity={onAddActivity}
        generatorTabState={generatorTabState}
        setGeneratorTabState={setGeneratorTabState}
      />
    );

  if (generatorTabState.selectedGeneratorNew) {
    if (isLoading) {
      return (
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr', justifyItems: 'center' }}
        >
          <ProgressBar title={t('Generating')} isSmallTemplates={true} />
        </div>
      );
    }
    return (
      <AITabContainer style={{ padding: 20, paddingTop: 0, overflow: 'auto' }}>
        {generatorTabState.selectedGeneratorNew.aiContent?.isPremiumGenerator && (
          <PlanTypeLabel
            userAccessBadge={userAccessBadge}
            generatorPlan={
              generatorTabState.selectedGeneratorNew.aiContent?.generatorPlan
            }
            isPremiumGenerator={
              generatorTabState.selectedGeneratorNew.aiContent?.isPremiumGenerator
            }
          />
        )}
        <div
          style={{
            display: 'grid',
            width: '100%',
            maxWidth: '500px',
            margin: 'auto',
            gridTemplateColumns: '1fr',
            padding: '10px 3px',
          }}
        >
          <GeneratorCard
            subTitle=""
            coverImageId={generatorTabState.selectedGeneratorNew?.coverImageId}
            title={generatorTabState.selectedGeneratorNew?.title || ''}
            description={generatorTabState.selectedGeneratorNew.description || ''}
            status={
              generatorTabState.selectedGeneratorNew?.status !== 'published' &&
              generatorTabState.selectedGeneratorNew?.creatorId ===
                userAccessBadge?.user.id
                ? 'Personal'
                : generatorTabState.selectedGeneratorNew?.status === 'staging'
                ? 'Staging'
                : undefined
            }
            learningStandards={generatorTabState.selectedGeneratorNew.learningStandards}
          />
        </div>

        <BaseModal
          visible={showShareModal}
          onCancel={() => setShowShareModal(false)}
          title={'Share this generator with...'}
        >
          <ShareGlobalLesson
            templateId={generatorTabState.selectedGeneratorNew.id || ''}
            isGeneratedByAI={false}
            isGenerator={true}
            title={generatorTabState.selectedGeneratorNew.title || ''}
          />
        </BaseModal>
        {generatorTabState.selectedGeneratorNew.description && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '10px 3px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', marginBottom: 3 }}>
                <h3 style={{ fontWeight: 'bold', flex: 1, marginBottom: 0 }}>
                  {t('What is this customizable lesson?')}
                </h3>
                <Tooltip mouseEnterDelay={1} title="Share this customizable lesson">
                  <IconWrapper
                    height="20px"
                    width="20px"
                    onClick={() => {
                      setShowShareModal(true);
                    }}
                  >
                    <ShareSvg />
                  </IconWrapper>
                </Tooltip>
              </div>
              {generatorTabState.selectedGeneratorNew.description &&
                generatorTabState.selectedGeneratorNew.description.length < 100 && (
                  <p
                    style={{
                      width: '100%',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {generatorTabState.selectedGeneratorNew.description}
                  </p>
                )}
              {generatorTabState.selectedGeneratorNew.description &&
                generatorTabState.selectedGeneratorNew.description.length >= 100 && (
                  <ShowMoreContainer>
                    <p
                      style={{
                        width: '100%',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {showMoreDescription
                        ? generatorTabState.selectedGeneratorNew.description
                        : generatorTabState.selectedGeneratorNew.description.slice(
                            0,
                            100,
                          )}
                      <ShowMoreButton
                        onClick={() => setShowMoreDescription()}
                        showMore={showMoreDescription}
                        numberOfComments={20}
                      />
                    </p>
                  </ShowMoreContainer>
                )}
            </div>
          </div>
        )}
        <GeneratorsForm
          isLoading={isLoading}
          isError={isError}
          handleConsumeGenerator={handleConsumeGenerator}
          selectedGenerator={generatorTabState.selectedGeneratorNew}
          formState={generatorTabState.generatorFormState}
        />
      </AITabContainer>
    );
  }
  return (
    <AITabContainer>
      <FixedSearchFilterContainer>
        <div style={{ width: '100%', padding: '0px 0px' }}>
          <SearchInput
            value={searchValue}
            onChange={onSearchInputChange}
            subjectId={subjectId}
            grades={grades}
            handleFilterChanged={handleFiltersChanged}
            subjectOptions={subjectOptions}
            gradeOptions={CURIPOD_FILTER_GRADE_OPTIONS}
            handleClearFilters={handleClearFilters}
          />
        </div>

        {!searchPhrase && activeCategory && (
          <GeneratorFilters
            onSelect={onCategoryChange}
            visibleCategories={visibleCategories}
            activeCategoryId={activeCategory.id}
          />
        )}
      </FixedSearchFilterContainer>
      {searchPhrase && (
        <div style={{ width: '100%', padding: 20 }}>
          <GeneratorGridView
            generatorTabState={generatorTabState}
            setGeneratorTabState={setGeneratorTabState}
            searchPhrase={searchPhrase}
            userAccessBadge={userAccessBadge}
          ></GeneratorGridView>
        </div>
      )}
      {!searchPhrase && (
        <>
          {activeCategory?.id === 'for_you' && (
            <ForYouView
              generatorTabState={generatorTabState}
              setGeneratorTabState={setGeneratorTabState}
              userAccessBadge={userAccessBadge}
            />
          )}
          {!activeCategory && (
            <GeneratorsContainer style={{ padding: '20px 20px' }}>
              <GeneratorGridView
                generatorTabState={generatorTabState}
                setGeneratorTabState={setGeneratorTabState}
                grades={grades}
                subjectId={subjectId}
                userAccessBadge={userAccessBadge}
              ></GeneratorGridView>
            </GeneratorsContainer>
          )}
          {activeCategory && activeCategory.id !== 'for_you' && (
            <>
              {activeCategory.subCategories.length === 0 && (
                <GeneratorsContainer style={{ padding: '20px 20px' }}>
                  <GeneratorGridView
                    generatorTabState={generatorTabState}
                    setGeneratorTabState={setGeneratorTabState}
                    category={activeCategory.id == 'all' ? undefined : activeCategory.id}
                    grades={grades}
                    subjectId={subjectId}
                    userAccessBadge={userAccessBadge}
                  ></GeneratorGridView>
                </GeneratorsContainer>
              )}
              {activeCategory.subCategories.length > 0 && (
                <GeneratorsContainer>
                  {activeCategory.subCategories?.map((subCategory, i) => (
                    <GeneratorsSlidingRows
                      key={i}
                      generatorTabState={generatorTabState}
                      setGeneratorTabState={setGeneratorTabState}
                      title={subCategory.title}
                      activeCategoryId={activeCategory.id}
                      activeSubcategoryId={subCategory.id}
                      grades={grades}
                      subjectId={subjectId}
                      userAccessBadge={userAccessBadge}
                    />
                  ))}
                </GeneratorsContainer>
              )}
            </>
          )}
        </>
      )}
    </AITabContainer>
  );
};

export const PremiumLabel = styled.div`
  background: #00000099;
  border-radius: 10px;
  color: ${theme.colors.white};
  font-weight: 600;
  font-size: 12px;
  padding: 2px 10px;
  height: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;
  width: fit-content;
`;

const ForYouView = ({
  setGeneratorTabState,
  generatorTabState,
  userAccessBadge,
}: {
  generatorTabState: GeneratorTabState;
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  userAccessBadge?: UserAccessBadge;
}) => {
  const isAITemplatesEnabled = useMixpanelFlag('AI_TEMPLATES');

  return (
    <GeneratorsContainer>
      <CustomGeneratorsSlidingRows
        generatorTabState={generatorTabState}
        setGeneratorTabState={setGeneratorTabState}
        title={'You might want to try'}
        userAccessBadge={userAccessBadge}
      />
      <RecentGeneratorsSlidingRows
        generatorTabState={generatorTabState}
        setGeneratorTabState={setGeneratorTabState}
        title={'Recent customizable lessons'}
        userAccessBadge={userAccessBadge}
      />
      {isAITemplatesEnabled && (
        <PersonalGeneratorsSlidingRows
          generatorTabState={generatorTabState}
          setGeneratorTabState={setGeneratorTabState}
          title={'Personal customizable lessons'}
          userAccessBadge={userAccessBadge}
        />
      )}
    </GeneratorsContainer>
  );
};

const GeneratorsContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 20px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 30px;
  height: calc(100% - 107px);
  overflow-y: auto;
`;

const GeneratorsSlidingRows = ({
  setGeneratorTabState,
  generatorTabState,
  title,
  activeCategoryId,
  activeSubcategoryId,
  grades,
  subjectId,
  userAccessBadge,
}: {
  generatorTabState: GeneratorTabState;
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  title: string;
  activeCategoryId: string;
  activeSubcategoryId: string;
  grades?: string[];
  subjectId?: string;
  userAccessBadge?: UserAccessBadge;
}) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const {
    generators,
    isFetchingGenerators,
    hasNextPageGenerators,
    fetchNextPageGenerators,
  } = useGeneratorsDesignerSearch({
    category: activeCategoryId,
    subCategory: activeSubcategoryId,
    grades,
    subjectId,
  });

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;
      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [generators]);

  const { ref: scrollRef, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPageGenerators && !isFetchingGenerators) {
      fetchNextPageGenerators();
    }
  }, [inView, hasNextPageGenerators, isFetchingGenerators, fetchNextPageGenerators]);

  return (
    <div style={{ marginBottom: 20 }}>
      <RowLabel>{title}</RowLabel>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconWrapper
                style={{ marginLeft: -5 }}
                color={theme.colors.black}
                height="32px"
                width="32px"
              >
                <ArrowLeftSvg />
              </IconWrapper>
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {generators.map(generator => {
            return (
              <HorisontalGeneratorCard
                key={generator.id}
                firstPublished={generator.firstPublished}
                coverImageId={generator.coverImageId}
                title={generator.title || ''}
                status={generator.status}
                onClick={() => {
                  setGeneratorTabState({
                    ...generatorTabState,
                    selectedGeneratorNew: generator,
                  });
                }}
                isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                generatorPlan={generator?.aiContent?.generatorPlan}
                userAccessBadge={userAccessBadge}
                description={generator.description || undefined}
                learningStandards={generator.learningStandards}
              />
            );
          })}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          <div ref={scrollRef} />
        </HorisontalScrollContainer>
        {showRightArrow && generators.length > 2 && (
          <GeneratorsRightArrowContainer onClick={handleRightArrowClicked}>
            <div
              style={{
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconWrapper
                style={{ marginRight: -5 }}
                color={theme.colors.black}
                height="32px"
                width="32px"
              >
                <ArrowRightSvg />
              </IconWrapper>
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </div>
  );
};

const CustomGeneratorsSlidingRows = ({
  setGeneratorTabState,
  generatorTabState,
  title,
  userAccessBadge,
}: {
  generatorTabState: GeneratorTabState;
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  title: string;
  userAccessBadge?: UserAccessBadge;
}) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const { generators, isFetchingGenerators } = useRecommendedGenerators();

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [generators]);

  if (!generators || generators?.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <RowLabel>{title}</RowLabel>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconWrapper
                style={{ marginLeft: -5 }}
                color={theme.colors.black}
                height="32px"
                width="32px"
              >
                <ArrowLeftSvg />
              </IconWrapper>
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {generators.map(generator => {
            return (
              <HorisontalGeneratorCard
                key={generator.id}
                firstPublished={generator.firstPublished}
                coverImageId={generator.coverImageId}
                title={generator.title || ''}
                status={generator.status}
                onClick={() => {
                  setGeneratorTabState({
                    ...generatorTabState,
                    selectedGeneratorNew: generator,
                  });
                }}
                isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                generatorPlan={generator?.aiContent?.generatorPlan}
                userAccessBadge={userAccessBadge}
                description={generator.description || undefined}
                learningStandards={generator.learningStandards}
              />
            );
          })}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
        </HorisontalScrollContainer>
        {showRightArrow && generators.length > 2 && (
          <GeneratorsRightArrowContainer onClick={handleRightArrowClicked}>
            <div
              style={{
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconWrapper
                style={{ marginRight: -5 }}
                color={theme.colors.black}
                height="32px"
                width="32px"
              >
                <ArrowRightSvg />
              </IconWrapper>
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </div>
  );
};

const ScrollMainContainer = styled.div`
  position: relative;
  width: 100%;
  height: 125px;
`;

const RecentGeneratorsSlidingRows = ({
  setGeneratorTabState,
  generatorTabState,
  title,
  userAccessBadge,
}: {
  generatorTabState: GeneratorTabState;
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  title: string;
  userAccessBadge?: UserAccessBadge;
}) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const { generators, isFetchingGenerators } = useRecentGenerators();
  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;
      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [generators]);

  if (!generators || generators?.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <RowLabel>{title}</RowLabel>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconWrapper
                style={{ marginLeft: -5 }}
                color={theme.colors.black}
                height="32px"
                width="32px"
              >
                <ArrowLeftSvg />
              </IconWrapper>
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {generators.map(generator => {
            return (
              <HorisontalGeneratorCard
                key={generator.id}
                firstPublished={generator.firstPublished}
                coverImageId={generator.coverImageId}
                title={generator.title || ''}
                status={generator.status}
                onClick={() => {
                  setGeneratorTabState({
                    ...generatorTabState,
                    selectedGeneratorNew: generator,
                  });
                }}
                isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                generatorPlan={generator?.aiContent?.generatorPlan}
                userAccessBadge={userAccessBadge}
                description={generator.description || undefined}
                learningStandards={generator.learningStandards}
              />
            );
          })}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
        </HorisontalScrollContainer>
        {showRightArrow && generators.length > 2 && (
          <GeneratorsRightArrowContainer onClick={handleRightArrowClicked}>
            <div
              style={{
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconWrapper
                style={{ marginRight: -5 }}
                color={theme.colors.black}
                height="32px"
                width="32px"
              >
                <ArrowRightSvg />
              </IconWrapper>
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </div>
  );
};

const PersonalGeneratorsSlidingRows = ({
  setGeneratorTabState,
  generatorTabState,
  title,
  userAccessBadge,
}: {
  generatorTabState: GeneratorTabState;
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  title: string;
  userAccessBadge?: UserAccessBadge;
}) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  const session = useSession();
  const {
    generators,
    isFetchingGenerators,
    hasNextPageGenerators,
    fetchNextPageGenerators,
  } = useGeneratorsDesignerSearch({
    creatorId: session.data?.user.uid,
  });

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;
      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [generators]);

  const { ref: scrollRef, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPageGenerators && !isFetchingGenerators) {
      fetchNextPageGenerators();
    }
  }, [inView, hasNextPageGenerators, isFetchingGenerators, fetchNextPageGenerators]);

  if (!generators || generators?.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <RowLabel>{title}</RowLabel>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconWrapper
                style={{ marginLeft: -5 }}
                color={theme.colors.black}
                height="32px"
                width="32px"
              >
                <ArrowLeftSvg />
              </IconWrapper>
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {generators.map(generator => {
            return (
              <HorisontalGeneratorCard
                key={generator.id}
                firstPublished={generator.firstPublished}
                coverImageId={generator.coverImageId}
                title={generator.title || ''}
                onClick={() => {
                  setGeneratorTabState({
                    ...generatorTabState,
                    selectedGeneratorNew: generator,
                  });
                }}
                isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                generatorPlan={generator?.aiContent?.generatorPlan}
                userAccessBadge={userAccessBadge}
                description={generator.description || undefined}
                learningStandards={generator.learningStandards}
              />
            );
          })}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          <div ref={scrollRef} />
        </HorisontalScrollContainer>
        {showRightArrow && generators.length > 2 && (
          <GeneratorsRightArrowContainer onClick={handleRightArrowClicked}>
            <div
              style={{
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconWrapper
                style={{ marginRight: -5 }}
                color={theme.colors.black}
                height="32px"
                width="32px"
              >
                <ArrowRightSvg />
              </IconWrapper>
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </div>
  );
};
const GeneratorGridView = ({
  searchPhrase,
  setGeneratorTabState,
  generatorTabState,
  category,
  grades,
  subjectId,
  userAccessBadge,
}: {
  searchPhrase?: string;
  generatorTabState: GeneratorTabState;
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  subjectId?: string;
  grades?: string[];
  category?: string;
  userAccessBadge?: UserAccessBadge;
}) => {
  const {
    generators,
    isFetchingGenerators,
    hasNextPageGenerators,
    fetchNextPageGenerators,
  } = useGeneratorsDesignerSearch({
    searchPhrase: searchPhrase,
    category,
    grades,
    subjectId,
  });
  const { ref: scrollRef, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPageGenerators && !isFetchingGenerators) {
      fetchNextPageGenerators();
    }
  }, [inView, hasNextPageGenerators, isFetchingGenerators, fetchNextPageGenerators]);
  return (
    <>
      <GeneratorGridViewContainer>
        {generators.map(generator => {
          return (
            <GeneratorCard
              key={generator.id}
              subTitle=""
              title={generator.title || ''}
              coverImageId={generator.coverImageId}
              status={generator.status}
              onClick={() =>
                setGeneratorTabState({
                  ...generatorTabState,
                  selectedGeneratorNew: generator,
                })
              }
              isPremiumGenerator={generator?.aiContent?.isPremiumGenerator || false}
              generatorPlan={generator?.aiContent?.generatorPlan || undefined}
              userAccessBadge={userAccessBadge}
              description={generator.description || ''}
              firstPublished={generator.firstPublished}
              learningStandards={
                generatorTabState?.selectedGeneratorNew?.learningStandards || []
              }
            />
          );
        })}
        {isFetchingGenerators && <Skeleton.Button active={true} />}
        {isFetchingGenerators && <Skeleton.Button active={true} />}
        {isFetchingGenerators && <Skeleton.Button active={true} />}
        {isFetchingGenerators && <Skeleton.Button active={true} />}
        <div ref={scrollRef} />
      </GeneratorGridViewContainer>
    </>
  );
};

const ShowMoreContainer = styled.div`
  display: flex;
  justify-content: start;
  align-items: baseline;

  padding-bottom: 10px;
`;

const RowLabel = styled.p`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  padding-left: 0px;
  color: ${theme.colors.greyDark};
`;

const HorisontalScrollContainer = styled.ul`
  width: 100%;
  height: 125px;
  overflow-x: auto;

  display: flex;
  gap: 10px;
  padding-top: 3px;
  padding-bottom: 15px;
  padding-left: 0px;
  padding-right: 0px;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }

  .ant-skeleton,
  .ant-skeleton-button {
    height: 100%;
    aspect-ratio: 1.28;
    border-radius: 5px;
    min-width: unset;
    width: unset;
  }
`;
const GeneratorGridViewContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0px;

  width: 100%;
  gap: 5px;
  .ant-skeleton-button {
    width: 150px;
    height: 145px;
    border-radius: 10px;
    min-width: unset;
  }
`;
export const BlankSlideSelector = ({
  handleCreateNewSlide,
}: {
  handleCreateNewSlide: (
    type: SlideType | SlideContentType | 'blank',
    replace?: boolean,
  ) => void;
}) => {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const [slideToCreate, setSlideToCreate] = useState<{
    type: SlideType;
    slide: Slide;
    contentType?: SlideContentType;
  }>({
    type: 'answers' as SlideType,
    slide: createNewEmptySlide({
      type: 'answers',
      textValue: 'Open question activity',
      includeMedia: true,
      textColor: theme.colors.black,
    }),
  });
  const activityOptions = useMemo(() => {
    return [
      {
        type: 'answers' as SlideType,
        title: 'Open question',
        slide: createNewEmptySlide({
          type: 'answers',
          textValue: 'Open question activity',
          includeMedia: true,
          textColor: theme.colors.black,
        }),
      },
      {
        type: 'canvas' as SlideType,
        slide: createNewEmptySlide({
          type: 'canvas',
          textValue: 'Drawing activity',
          textColor: theme.colors.black,
          includeMedia: true,
        }),
        title: 'Drawing',
      },
      {
        type: 'poll' as SlideType,
        title: 'Poll',
        includeMedia: false,

        slide: createNewEmptySlide({
          type: 'poll',
          textValue: 'Poll activity',
          textColor: theme.colors.black,
        }),
      },
      {
        type: 'wordcloud' as SlideType,
        title: 'Wordcloud',
        slide: createNewEmptySlide({
          type: 'wordcloud',
          textValue: 'Wordcloud activity',
          textColor: theme.colors.black,
          includeMedia: true,
        }),
      },
      {
        type: 'aiAssessedAnswers' as SlideType,
        title: 'AI Feedback',
        slide: createNewEmptySlide({
          type: 'aiAssessedAnswers',
          textValue: 'AI feedback for students',
          textColor: theme.colors.black,
          includeMedia: true,
        }),
      },
      {
        type: 'aiAssessedImageAnswers' as SlideType,
        title: 'AI Whiteboard',
        slide: createNewEmptySlide({
          type: 'aiAssessedImageAnswers',
          textValue: 'AI feedback on whiteboarding',
          textColor: theme.colors.black,
          includeMedia: true,
        }),
      },
    ];
  }, []);
  const contentSlidesOptions = useMemo(() => {
    return [
      {
        type: 'slide' as SlideType,
        contentType: 'header' as SlideContentType,
        slide: createNewEmptySlide({
          type: 'slide',
          contentType: 'header',
          textColor: theme.colors.black,
        }),
        includeMedia: true,

        title: 'Header',
      },
      {
        type: 'slide' as SlideType,
        contentType: 'image' as SlideContentType,
        slide: createNewEmptySlide({
          type: 'slide',
          contentType: 'image',
          textColor: theme.colors.black,
        }),
        includeMedia: true,
        title: 'Image',
      },
      {
        type: 'slide' as SlideType,
        contentType: 'header_text' as SlideContentType,
        slide: createNewEmptySlide({
          type: 'slide',
          contentType: 'header_text',
          textColor: theme.colors.black,
        }),
        includeMedia: true,
        title: 'Header and text',
      },
      {
        type: 'slide' as SlideType,
        contentType: 'fullscreen_media' as SlideContentType,
        slide: createNewEmptySlide({
          type: 'slide',
          contentType: 'fullscreen_media',
          fullscreenThumbnail:
            'https://storage.googleapis.com/curipod-images-cdn-bucket/full_screen.png',
        }),
        includeMedia: true,
        title: 'Fullscreen media',
      },
      {
        type: 'slide' as SlideType,
        contentType: 'image_text' as SlideContentType,
        slide: createNewEmptySlide({
          type: 'slide',
          contentType: 'image_text',
          textColor: theme.colors.black,
        }),
        includeMedia: true,
        title: 'Image and text',
      },
      {
        type: 'slide' as SlideType,
        contentType: 'text_image' as SlideContentType,
        slide: createNewEmptySlide({
          type: 'slide',
          contentType: 'text_image',
          textColor: theme.colors.black,
        }),
        includeMedia: true,
        title: 'Text and image',
      },
    ];
  }, []);
  const onSlideClicked = (type: SlideType, subType?: SlideContentType) => {
    if (type === 'slide') {
      handleCreateNewSlide(subType || 'blank', false);
      dispatch({
        type: DesignerUIStateActions.setSelectedTab,
        payload: {
          tab: 'media',
          subTab: subType === 'fullscreen_media' ? 'appSmash' : undefined,
        },
      });
    } else {
      handleCreateNewSlide(type, false);
      dispatch({
        type: DesignerUIStateActions.setSelectedTab,
        payload: {
          tab: 'interactiveElements',
        },
      });
    }
  };
  const randomPollResult = Array.from(
    { length: 10 },
    () => Math.floor(Math.random() * 10) + 1,
  );

  return (
    <BlankSlideContainer>
      <ContentSlidesContainer>
        <ContentDesignerSlideItem key={slideToCreate.type + slideToCreate}>
          <SlideWrapperViwer
            aspect={{ width: 1920, height: 1080 }}
            isDiscoverTemplate={false}
            showStaticPoll={true}
            staticPollResult={randomPollResult}
            slide={slideToCreate.slide}
            backgroundMedia={slideToCreate.slide.backgroundMedia}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: zIndexes('ACTIVITY_OVERLAY_ITEM'),
              }}
            >
              {slideToCreate.slide.type !== 'slide' && (
                <>
                  <ActivityOverlayItem>
                    <IconWrapper color="white" height={'150px'} width={'150px'}>
                      {slideTypeToIcon(slideToCreate.slide.type)}
                    </IconWrapper>
                  </ActivityOverlayItem>
                </>
              )}
            </div>
          </SlideWrapperViwer>
        </ContentDesignerSlideItem>
      </ContentSlidesContainer>
      <SelectorButtonContainer>
        <h3 style={{ width: '100%', gridColumn: '1/3' }}>Activities</h3>

        {activityOptions.map((option, i) => (
          <BlankSlideButton
            type="ghost"
            key={i}
            onMouseEnter={() => setSlideToCreate(option)}
            onClick={() => onSlideClicked(option.type)}
            icon={<IconWrapper>{toIcon(option.type).icon}</IconWrapper>}
          >
            {option.title}
          </BlankSlideButton>
        ))}
        <h3 style={{ width: '100%', gridColumn: '1/3', marginTop: '20px' }}>
          Content slides
        </h3>

        {contentSlidesOptions.map((option, i) => (
          <BlankSlideButton
            type="ghost"
            key={i}
            onMouseEnter={() => setSlideToCreate(option)}
            onClick={() => onSlideClicked('slide', option.contentType)}
            icon={<IconWrapper>{toIcon(option.contentType).icon}</IconWrapper>}
          >
            {option.title}
          </BlankSlideButton>
        ))}
      </SelectorButtonContainer>
    </BlankSlideContainer>
  );
};

const FixedSearchFilterContainer = styled(TabContainer)`
  height: fit-content;
  padding-left: 20px;
  padding-right: 20px;
`;

const AITabContainer = styled(TabContainer)`
  padding: 0px 0px 0px 0px;
  height: 100%;
`;
const BlankSlideContainer = styled(TabContainer)`
  padding: 0px 0px 0px 0px;
  height: 100%;
  overflow-y: auto;
`;

const SelectorButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 150px 150px;
  gap: 5px;
  justify-content: center;
  width: 100%;
`;
const BlankSlideButton = styled(Button)`
  display: flex;
  justify-content: flex-start;
  padding: 10px;
  font-size: 12px;
`;
export const ActivityOverlayItem = styled.div`
  background-color: rgba(122, 122, 122, 0.76);
  position: absolute;
  top: 100;
  left: 100;
  border-radius: 4px 0 4px 0;
  padding: 40px;
  cursor: default !important;
  border-radius: 10px;
  z-index: ${zIndexes('ACTIVITY_OVERLAY_ITEM')};
  svg {
    cursor: default !important;
  }
`;
const ContentSlidesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 auto;
  padding: 2px 20px 20px 20px;
  width: 100%;
  gap: 10px;
  max-width: 400px;
`;
const ContentDesignerSlideItem = styled.div`
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  cursor: default;
`;

const GeneratorTabWrapper = ({
  shouldShowBackButton,
  children,
}: {
  shouldShowBackButton: boolean;
  children: ReactNode;
}) => {
  if (shouldShowBackButton) {
    return (
      <div style={{ height: '100%', overflow: 'hidden', paddingBottom: 70 }}>
        {children}
      </div>
    );
  }
  return <>{children}</>;
};

const GeneratorsTab = ({
  onAddActivity,
  selectedSubTab,
  handleCreateNewSlide,
  setGeneratorTabState,
}: {
  onAddActivity: (rounds: Slide[]) => void;
  handleCreateNewSlide: (
    type: SlideType | SlideContentType | 'blank',
    replace?: boolean | undefined,
  ) => void;
  selectedSubTab?: TabState['subTab'];
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
}) => {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const generatorTabState = useDesignerUIContext(
    context => context.state.generatorTabState,
  );
  const { consumeGenerator, isLoading, isError } = useConsumeGenerator();

  const shouldShowBackButton = useMemo(() => {
    return (
      generatorTabState.selectedGeneratorNew !== undefined &&
      generatorTabState.generatedSlides.length === 0 &&
      !isLoading &&
      selectedSubTab !== 'newBlankSlide'
    );
  }, [
    generatorTabState.selectedGeneratorNew,
    generatorTabState.generatedSlides.length,
    isLoading,
    selectedSubTab,
  ]);

  return (
    <GeneratorTabWrapper shouldShowBackButton={shouldShowBackButton}>
      {shouldShowBackButton && (
        <BackTitleGrid>
          <span
            style={{
              display: 'flex',
              cursor: 'pointer',
              alignItems: 'center',
              gridColumn: '1 / -1',
              fontSize: 18,
              margin: '20px 0px 10px 10px',
              color: theme.colors.greyVeryDark,
            }}
            onClick={() =>
              setGeneratorTabState({
                ...generatorTabState,
                isLoadingGeneratedRounds: false,
                isErrorGeneratedRounds: undefined,
                selectedGeneratorNew: undefined,
                generatorFormState: undefined,
              })
            }
          >
            <IconWrapper height="30px" width="30px">
              <ArrowLeftSvg />
            </IconWrapper>
            Back to customizable lessons
          </span>
        </BackTitleGrid>
      )}
      <GeneratorContaienr shouldShowBackButton={shouldShowBackButton}>
        <GeneratorTabs
          tabBarStyle={{
            display:
              isLoading ||
              (generatorTabState.generatedSlides.length !== 0 &&
                selectedSubTab !== 'newBlankSlide') ||
              shouldShowBackButton
                ? 'none'
                : 'flex',
          }}
          defaultActiveKey="newBlankSlide"
          centered
          size="large"
          onTabClick={key => {
            if (key === selectedSubTab || !!generatorTabState.selectedGeneratorNew) {
              setGeneratorTabState({
                ...generatorTabState,
                isLoadingGeneratedRounds: false,
                isErrorGeneratedRounds: undefined,
                selectedGeneratorNew: undefined,
                generatorFormState: undefined,
              });
            }
          }}
          activeKey={selectedSubTab || 'newBlankSlide'}
          onChange={key => {
            dispatch({
              type: DesignerUIStateActions.setSelectedTab,
              payload: {
                tab: 'generators',
                subTab: key as 'generators' | 'newBlankSlide',
              },
            });
          }}
        >
          <GeneratorTabPane tab="Create your own" key="newBlankSlide">
            <BlankSlideSelector handleCreateNewSlide={handleCreateNewSlide} />
          </GeneratorTabPane>
          <GeneratorTabPane tab="Generate with AI" key="generators">
            <DesignerGeneratorSelector
              generatorTabState={generatorTabState}
              onAddActivity={onAddActivity}
              setGeneratorTabState={setGeneratorTabState}
              consumeGenerator={consumeGenerator}
              isLoading={isLoading}
              isError={isError}
            />
          </GeneratorTabPane>
        </GeneratorTabs>
      </GeneratorContaienr>
    </GeneratorTabWrapper>
  );
};
const GeneratorTabs = styled(Tabs)<{ $noMargin?: boolean }>`
  color: ${theme.colors.black};
  font-weight: bold;
  &&-active &-btn {
    text-shadow: 0 0 0.25px currentcolor;
  }
  .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap::after,
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,
  .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap::before {
    box-shadow: none !important;
    color: ${theme.colors.orange} !important;
  }
  .ant-tabs-content {
    height: 100% !important;
  }
  &:hover,
  &:active,
  &:focus {
    .ant-tabs-tab {
      color: ${theme.colors.black};
    }
  }
  .ant-tabs-ink-bar {
    background: ${theme.colors.orange};
  }
  height: 100%;

  ${({ $noMargin }) =>
    $noMargin &&
    css`
      .ant-tabs-nav {
        margin-bottom: 0px;
      }
    `}
`;
const GeneratorTabPane = styled(Tabs.TabPane)`
  height: 100%;
`;
const GeneratorContaienr = styled(TabContainer)<{ shouldShowBackButton: boolean }>`
  padding: 0px 0px 0px 0px;
  height: 100%;

  ${({ shouldShowBackButton }) =>
    shouldShowBackButton &&
    css`
      overflow: auto;
    `}
`;

export default GeneratorsTab;
